import { useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useResourceBundle,
  useResourceFiles,
  useRegisterResourceFile,
  useConfirmResourceFile,
  useDeleteResourceFile,
  usePublishResourceBundle,
  useResourceVersions,
  gzipBlob,
  isGzipBlob,
  suggestGzip,
  supportsGzipCompression,
  withGzSuffix,
} from "../../api/resources";
import { uploadToS3, computeSHA256 } from "../../api/firmware";
import { extractErrorMessage } from "../../api/errors";
import { StatusBadge } from "../../components/StatusBadge";
import type { ResourceFile } from "../../api/types";
import { Rocket, Trash2, UploadCloud } from "lucide-react";

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

// gzipForGzPath compresses content headed for a .gz path. A .gz path is
// served with Content-Encoding: gzip, so its bytes must be gzip whatever
// the checkbox says.
async function gzipForGzPath(input: Blob): Promise<Blob> {
  if (!supportsGzipCompression()) {
    throw new Error("This browser cannot gzip files. Pick a file that is already gzip-compressed for a .gz path.");
  }
  return gzipBlob(input);
}

function deployUrl(bundleId: string, version: number): string {
  return `/tasks/create?resourceBundleId=${encodeURIComponent(bundleId)}&resourceBundleVersion=${version}`;
}

export function ResourceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const bundleId = id ?? "";

  const { data: bundleResp, isLoading: bundleLoading } = useResourceBundle(bundleId);
  // Every working-set file, so the publish gate checks all of them.
  const { data: filesResp, isLoading: filesLoading } = useResourceFiles(bundleId);
  const versionsQuery = useResourceVersions(bundleId, { limit: 20 });
  const registerFile = useRegisterResourceFile(bundleId);
  const confirmFile = useConfirmResourceFile(bundleId);
  const deleteFile = useDeleteResourceFile(bundleId);
  const publishBundle = usePublishResourceBundle(bundleId);

  const [uploadPath, setUploadPath] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [gzipEnabled, setGzipEnabled] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadNotice, setUploadNotice] = useState("");
  const [publishError, setPublishError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ResourceFile | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [replacingFileId, setReplacingFileId] = useState<string | null>(null);
  const [pickedIsGzip, setPickedIsGzip] = useState(false);
  // The file whose magic bytes are being checked, so a slow check for an
  // earlier pick can't overwrite the result for a later one.
  const pickedFileRef = useRef<File | null>(null);

  const bundle = bundleResp?.data;
  const files = filesResp?.data ?? [];
  const versions = versionsQuery.data?.pages.flatMap((page) => page.data ?? []) ?? [];
  const allFilesActive = files.length > 0 && files.every((f) => f.status === "active");

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!uploadFile) return;
    setUploadError("");
    setUploadNotice("");
    setUploading(true);
    try {
      const contentType = uploadFile.type || "application/octet-stream";
      let path = uploadPath.trim();
      let body: Blob = uploadFile;

      if (await isGzipBlob(uploadFile)) {
        // Already compressed (e.g. data/index.html.gz from the firmware
        // project). Compressing again would leave gzip-of-gzip on the
        // device, so upload the bytes untouched; the path must then end in
        // .gz so the device serves it with Content-Encoding: gzip.
        path = withGzSuffix(path);
        setUploadNotice(`File is already gzip-compressed — uploaded as-is and stored as ${path}.`);
      } else if (path.endsWith(".gz")) {
        body = await gzipForGzPath(uploadFile);
        setUploadNotice(`Compressed ${formatSize(uploadFile.size)} → ${formatSize(body.size)} to match the .gz path.`);
      } else if (gzipEnabled) {
        const compressed = await gzipBlob(uploadFile);
        // Gzip's fixed header/footer overhead can make already-tiny files
        // larger, not smaller. Only commit to the .gz path (and the
        // encoding the device will then be locked into forever, since
        // path is immutable across a later replace) when it actually helps.
        if (compressed.size < uploadFile.size) {
          body = compressed;
          path = withGzSuffix(path);
          setUploadNotice(
            `Compressed ${formatSize(uploadFile.size)} → ${formatSize(body.size)} (${Math.round((1 - body.size / uploadFile.size) * 100)}% smaller)`,
          );
        } else {
          setUploadNotice(
            `Gzip did not reduce size for this file (${formatSize(uploadFile.size)} → ${formatSize(compressed.size)}) — uploaded uncompressed instead.`,
          );
        }
      }

      const res = await registerFile.mutateAsync({ path, fileSize: body.size, contentType });
      if (!res.data.uploadUrl) {
        throw new Error("Upload URL was not returned.");
      }
      await uploadToS3(res.data.uploadUrl, body);
      const checksum = await computeSHA256(body);
      await confirmFile.mutateAsync({ fileId: res.data.fileId, checksum });
      setUploadPath("");
      setUploadFile(null);
      setPickedIsGzip(false);
    } catch (err) {
      setUploadError(extractErrorMessage(err, "Failed to upload file"));
    } finally {
      setUploading(false);
    }
  }

  async function handleReplace(file: ResourceFile, newFile: File) {
    setUploadError("");
    setReplacingFileId(file.fileId);
    try {
      // A file's path is fixed across a replace (only its fileId/content
      // changes), so whether the stored bytes are gzip isn't a fresh
      // choice here — it's whatever the existing path already commits to.
      // Check before starting the replace so a refusal leaves the file
      // untouched on the server.
      const pickedIsGzipped = await isGzipBlob(newFile);
      let body: Blob = newFile;
      if (file.path.endsWith(".gz")) {
        if (!pickedIsGzipped) body = await gzipForGzPath(newFile);
      } else if (pickedIsGzipped) {
        throw new Error(
          `${newFile.name} is already gzip-compressed, but ${file.path} is not a .gz path, so the device would serve it without decompressing it. Pick the uncompressed file instead.`,
        );
      }

      const started = await confirmFile.mutateAsync({ fileId: file.fileId, replace: true });
      if (!started.data.uploadUrl) {
        throw new Error("Upload URL was not returned.");
      }
      await uploadToS3(started.data.uploadUrl, body);
      const checksum = await computeSHA256(body);
      await confirmFile.mutateAsync({ fileId: started.data.fileId, checksum });
    } catch (err) {
      setUploadError(extractErrorMessage(err, "Failed to replace file"));
    } finally {
      setReplacingFileId(null);
    }
  }

  async function handleDeleteFile() {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await deleteFile.mutateAsync(deleteTarget.fileId);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "Failed to delete file"));
    }
  }

  async function handlePublish() {
    setPublishError("");
    try {
      await publishBundle.mutateAsync();
    } catch (err) {
      setPublishError(extractErrorMessage(err, "Failed to publish bundle"));
    }
  }

  if (bundleLoading) {
    return <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading bundle...</div>;
  }
  if (!bundle) {
    return <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Bundle not found.</div>;
  }

  return (
    <div>
      <button onClick={() => navigate("/resources")} className="mb-4 text-sm text-riot-gold-500 hover:underline">
        &larr; Back to resources
      </button>

      <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{bundle.name}</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-riot-navy-300">{bundle.model}</p>
            {bundle.description && <p className="mt-2 text-sm text-gray-600 dark:text-riot-navy-200">{bundle.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={bundle.status} />
            {bundle.latestVersion > 0 && (
              <button
                onClick={() => navigate(deployUrl(bundle.bundleId, bundle.latestVersion))}
                className="inline-flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
              >
                <Rocket size={14} /> Deploy v{bundle.latestVersion}
              </button>
            )}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><dt className="text-gray-500 dark:text-riot-navy-300">Files</dt><dd className="font-medium">{bundle.fileCount}</dd></div>
          <div><dt className="text-gray-500 dark:text-riot-navy-300">Working set size</dt><dd className="font-medium">{formatSize(bundle.workingSetSize)}</dd></div>
          <div><dt className="text-gray-500 dark:text-riot-navy-300">Latest version</dt><dd className="font-medium">{bundle.latestVersion > 0 ? `v${bundle.latestVersion}` : "unpublished"}</dd></div>
          <div><dt className="text-gray-500 dark:text-riot-navy-300">Updated</dt><dd className="font-medium">{new Date(bundle.updatedAt).toLocaleString()}</dd></div>
        </dl>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Working set</h3>
          <button
            onClick={handlePublish}
            disabled={!allFilesActive || publishBundle.isPending}
            title={!allFilesActive ? "All files must finish uploading before publishing" : undefined}
            className="rounded-md border border-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-gold-600 hover:bg-riot-gold-50 disabled:opacity-50 dark:text-riot-gold-400 dark:hover:bg-riot-navy-700"
          >
            {publishBundle.isPending ? "Publishing..." : "Publish new version"}
          </button>
        </div>
        {publishError && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{publishError}</p>}

        <form onSubmit={handleUpload} className="mt-4 flex flex-wrap items-end gap-3 rounded-md border border-dashed border-gray-300 p-4 dark:border-riot-navy-600">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">Device path</label>
            <input
              aria-label="Device path"
              type="text"
              value={uploadPath}
              onChange={(e) => setUploadPath(e.target.value)}
              placeholder="e.g. index.html"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">File</label>
            <input
              aria-label="File"
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                setUploadFile(file);
                setPickedIsGzip(false);
                pickedFileRef.current = file;
                if (file) {
                  setGzipEnabled(supportsGzipCompression() && suggestGzip(file));
                  void isGzipBlob(file).then((gz) => {
                    if (pickedFileRef.current === file) setPickedIsGzip(gz);
                  });
                }
              }}
              className="text-sm"
              required
            />
          </div>
          {supportsGzipCompression() && (
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                <input
                  type="checkbox"
                  checked={gzipEnabled}
                  onChange={(e) => setGzipEnabled(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-riot-gold-500 focus:ring-riot-gold-500"
                />
                Gzip before upload
              </label>
              {pickedIsGzip ? (
                <span className="text-[11px] text-gray-500 dark:text-riot-navy-400">
                  Gzip file detected — not compressed again{uploadPath.trim() ? `, stored as ${withGzSuffix(uploadPath.trim())}` : ""}
                </span>
              ) : gzipEnabled && uploadPath.trim() && (
                <span className="text-[11px] text-gray-500 dark:text-riot-navy-400">
                  Stored as {withGzSuffix(uploadPath.trim())}
                </span>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={!uploadPath.trim() || !uploadFile || uploading}
            className="inline-flex items-center gap-1.5 rounded bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
          >
            <UploadCloud size={14} /> {uploading ? "Uploading..." : "Add file"}
          </button>
        </form>
        {uploadNotice && <p className="mt-2 text-sm text-green-600 dark:text-green-400">{uploadNotice}</p>}
        {uploadError && <p className="mt-2 text-sm text-red-600 dark:text-red-300">{uploadError}</p>}

        <div className="mt-4 overflow-x-auto">
          {filesLoading ? (
            <p className="py-6 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading files...</p>
          ) : files.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400 dark:text-riot-navy-400">No files in the working set yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500 dark:border-riot-navy-700 dark:text-riot-navy-400">
                  <th className="py-2">Path</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Size</th>
                  <th className="py-2">Updated</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map((file) => (
                  <tr key={file.fileId} className="border-b border-gray-100 dark:border-riot-navy-700/60">
                    <td className="py-2 font-mono text-xs">
                      {file.path}
                      {file.path.endsWith(".gz") && (
                        <>
                          {" "}
                          <span className="ml-2 rounded-full bg-riot-gold-100 px-1.5 py-0.5 text-[10px] font-medium text-riot-gold-600 dark:bg-riot-gold-500/20 dark:text-riot-gold-400">
                            gzip
                          </span>
                        </>
                      )}
                    </td>
                    <td className="py-2"><StatusBadge status={file.status} /></td>
                    <td className="py-2">{formatSize(file.size)}</td>
                    <td className="py-2 text-xs text-gray-500 dark:text-riot-navy-400">{new Date(file.updatedAt).toLocaleString()}</td>
                    <td className="py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <label
                          className="cursor-pointer rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-riot-navy-700 dark:hover:text-riot-gold-400"
                          title="Replace content"
                        >
                          <UploadCloud size={14} />
                          <input
                            aria-label={`Replace ${file.path}`}
                            type="file"
                            className="hidden"
                            disabled={replacingFileId === file.fileId}
                            onChange={(e) => {
                              const newFile = e.target.files?.[0];
                              if (newFile) void handleReplace(file, newFile);
                              e.target.value = "";
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => { setDeleteTarget(file); setDeleteError(""); }}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-300"
                          aria-label={`Delete ${file.path}`}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-riot-navy-700 dark:bg-riot-navy-800">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Version history</h3>
        {versions.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400 dark:text-riot-navy-400">No published versions yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-100 dark:divide-riot-navy-700/60">
            {versions.map((v) => (
              <li key={v.version} className="flex items-center justify-between py-2 text-sm">
                <span>
                  <span className="font-medium text-gray-900 dark:text-white">v{v.version}</span>
                  <span className="ml-2 text-gray-500 dark:text-riot-navy-300">
                    {v.files.length} file{v.files.length === 1 ? "" : "s"} &middot; {formatSize(v.totalSize)}
                  </span>
                </span>
                <button onClick={() => navigate(deployUrl(bundleId, v.version))} className="text-xs font-medium text-riot-gold-500 hover:underline">
                  Deploy this version
                </button>
              </li>
            ))}
          </ul>
        )}
        {versionsQuery.hasNextPage && (
          <button
            type="button"
            onClick={() => versionsQuery.fetchNextPage()}
            disabled={versionsQuery.isFetchingNextPage}
            className="mt-3 text-xs font-medium text-riot-gold-600 hover:text-riot-gold-700 disabled:opacity-50 dark:text-riot-gold-400"
          >
            {versionsQuery.isFetchingNextPage ? "Loading…" : "Load older versions"}
          </button>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Delete file confirmation">
          <div className="w-full max-w-md rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Remove file?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-riot-navy-200">
              {deleteTarget.path} will be removed from the working set. Published versions are unaffected.
            </p>
            {deleteError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{deleteError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteFile}
                disabled={deleteFile.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
              >
                {deleteFile.isPending ? "Removing..." : "Remove file"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
